import React from 'react';
import classNames from 'classnames';
import Loader from '@/components/common/Loader';
import DataList, { DataListTableHeader } from '@/modules/DataList';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { useEntityDetails } from '@/modules/EntityDetails';
import { useQueryProvider } from '@/modules/QueryProvider';
import OpportunitiesSortableFields from '../constants/OpportunitiesSortableFields';
import useOpportunitiesDataListColumns from '../hooks/useOpportunitiesDataListColumns';
import useOpportunitiesDataListData from '../hooks/useOpportunitiesDataListData';
import type OpportunitiesDataListRow from '../types/OpportunitiesDataListRow';
import OpportunitiesEmptyList from './OpportunitiesEmptyList';
import OpportunitySummaries from './OpportunitySummaries';

export default function OpportunitiesDataList({
  isReport,
}: {
  isReport?: boolean;
}) {
  const { openOpportunityEntity } = useEntityDetails();
  const { isLagging, isLoading, opportunities, totalHits } =
    useOpportunitiesDataListData();
  const handleRowClick = React.useCallback((row: OpportunitiesDataListRow) => {
    openOpportunityEntity(row.opportunityId);
  }, []);
  const { query } = useQueryProvider();

  const isShowAllOpportunities = Boolean(query.archived !== 'all');

  const columns = useOpportunitiesDataListColumns(
    isReport
      ? {
          showActions: false,
          shouldRemoveOnArchive: isShowAllOpportunities,
          shouldRemoveOnUnarchive: isShowAllOpportunities,
        }
      : {
          shouldRemoveOnArchive: isShowAllOpportunities,
          shouldRemoveOnUnarchive: isShowAllOpportunities,
        },
  );

  if (isLoading) {
    return <Loader isCentered />;
  }

  if (!opportunities.length) {
    return <OpportunitiesEmptyList />;
  }

  return (
    <>
      {isReport && (
        <DataListTableHeader
          text="OPPORTUNITIES_TABLE_HEADER"
          count={totalHits}
          className="mb-6"
        />
      )}
      {!isReport && <OpportunitySummaries />}
      <DataList<OpportunitiesDataListRow>
        TableRowProps={({ archivedAt }) => ({
          onClick: handleRowClick,
          className: classNames('cursor-pointer', {
            'italic [&_td]:text-text-disabled [&_td]:hover:text-text-secondary':
              archivedAt,
          }),
          hover: true,
        })}
        columns={columns}
        data={opportunities}
        isLagging={isLagging}
        rowKey="opportunityId"
        sortableFields={OpportunitiesSortableFields}
        totalRows={totalHits}
      />
    </>
  );
}
